// apps/client/src/components/common/EnhancedDashboardFilters.tsx
// Enhanced filtering UI with feature flag support
// Extends base DashboardFilters with additional filter dimensions

import React, { useMemo } from 'react';
import { featureFlags } from '../../config/featureFlags';
import type { EnhancedDashboardFilters as EnhancedFiltersType } from '../../types';
import { getBrokerDisplayName } from '../../utils/dataProcessing';

interface EnhancedDashboardFiltersProps {
  brokers: string[];
  lenders?: string[]; // ✅ NEW: Available lenders
  filters: EnhancedFiltersType;
  setFilters: React.Dispatch<React.SetStateAction<EnhancedFiltersType>>;
  userRole: string;
  isOfficeDashboard: boolean;
  hideCommissionType?: boolean; // ✅ OFFICE: Hide commission type filter
  hidePerformance?: boolean; // ✅ OFFICE: Hide performance filter
  earliestYear?: number; // ✅ DYNAMIC: First year with commission data
  latestYear?: number; // ✅ DYNAMIC: Most recent year with data
}

/**
 * Enhanced Dashboard Filters
 *
 * Extends base dashboard filters with additional dimensions:
 * - Lender filtering
 * - Commission type filtering
 * - Performance bands
 *
 * All enhancements are feature-flagged for safe rollout.
 */
const EnhancedDashboardFilters: React.FC<EnhancedDashboardFiltersProps> = ({
  brokers,
  lenders = [],
  filters,
  setFilters,
  userRole,
  isOfficeDashboard,
  hideCommissionType = false,
  hidePerformance = false,
  earliestYear,
  latestYear,
}) => {
  const handleFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement> | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target;

    if (name === 'yearType') {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      let defaultYear: number;

      if (value === 'financial') {
        defaultYear = currentMonth >= 6 ? currentYear : currentYear - 1;
      } else {
        defaultYear = currentYear;
      }

      setFilters((prev) => ({ ...prev, yearType: value as 'calendar' | 'financial', year: defaultYear }));
    } else {
      // <select> always yields a string; year must stay a number (FilterType.year,
      // and the callables' strict year schema rejects "2025" with invalid-argument).
      setFilters((prev) => ({ ...prev, [name]: name === 'year' ? Number(value) : value }));
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years: { value: number; label: string | number }[] = [];

    // Use dynamic range if available, otherwise default to 5 years
    // ✅ FIX (2026-01-01): Always include current year even if no data exists yet
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);

    if (filters.yearType === 'financial') {
      const currentMonth = new Date().getMonth();
      // For financial years, start from current FY and go back to earliest data
      const startYear = currentMonth >= 6 ? dynamicLatestYear : dynamicLatestYear - 1;
      // Go back to earliest year that could have data in a financial year
      const endYear = dynamicEarliestYear;
      for (let year = startYear; year >= endYear; year--) {
        years.push({ value: year, label: `${year}/${year + 1}` });
      }
    } else {
      // Calendar years: from latest to earliest
      for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
        years.push({ value: year, label: year });
      }
    }
    return years;
  };

  const yearOptions = useMemo(generateYearOptions, [filters.yearType, earliestYear, latestYear]);

  const canSeeAdminFilters =
    isOfficeDashboard && ['super_admin', 'office_admin', 'administrator'].includes(userRole);

  return (
    <div className="bg-gray-800 p-4 rounded-lg shadow-md mb-6">
      {/* Row 1: Base filters */}
      <div className="flex flex-wrap items-end gap-4">
        {/* Period Type */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Period Type</label>
          <div className="flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
            <button
              onClick={() => handleFilterChange({ target: { name: 'yearType', value: 'calendar' } })}
              className={`px-3 py-1 text-sm rounded-md w-full ${
                filters.yearType === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-300'
              }`}
            >
              Calendar
            </button>
            <button
              onClick={() => handleFilterChange({ target: { name: 'yearType', value: 'financial' } })}
              className={`px-3 py-1 text-sm rounded-md w-full ${
                filters.yearType === 'financial' ? 'bg-blue-600 text-white' : 'text-gray-300'
              }`}
            >
              Financial
            </button>
          </div>
        </div>

        {/* Year */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1">Year</label>
          <select
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
            className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
          >
            {yearOptions.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </select>
        </div>

        {/* Activity Status (Admin only) */}
        {canSeeAdminFilters && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Activity Status</label>
            <div className="flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
              <button
                onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'all' } })}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.activityStatus === 'all' ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'active' } })}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.activityStatus === 'active' ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => handleFilterChange({ target: { name: 'activityStatus', value: 'inactive' } })}
                className={`px-3 py-1 text-sm rounded-md ${
                  filters.activityStatus === 'inactive' ? 'bg-blue-600 text-white' : 'text-gray-300'
                }`}
              >
                Non-Active
              </button>
            </div>
          </div>
        )}

        {/* Broker (Admin only) */}
        {canSeeAdminFilters && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">Broker</label>
            <select
              name="broker"
              value={filters.broker}
              onChange={handleFilterChange}
              className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 min-w-[180px]"
            >
              <option value="all">All Brokers</option>
              {brokers.map((b) => (
                <option key={b} value={b}>
                  {getBrokerDisplayName(b)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Lender Filter (moved inline for office dashboard) */}
        {featureFlags.enhancedFilters && featureFlags.lenderFilter && lenders.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Lender
              <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 rounded">NEW</span>
            </label>
            <select
              name="lender"
              value={filters.lender || 'all'}
              onChange={handleFilterChange}
              className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 min-w-[180px]"
            >
              <option value="all">All Lenders</option>
              {lenders.map((lender) => (
                <option key={lender} value={lender}>
                  {lender}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Row 2: Enhanced filters (feature-flagged) */}
      {featureFlags.enhancedFilters && (
        <div className="flex flex-wrap items-end gap-4 mt-4 pt-4 border-t border-gray-700">
          {/* Commission Type Filter */}
          {featureFlags.commissionTypeFilter && !hideCommissionType && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Commission Type
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 rounded">NEW</span>
              </label>
              <div className="flex gap-x-2 bg-gray-700 border-gray-600 rounded-md p-1">
                <button
                  onClick={() => handleFilterChange({ target: { name: 'commissionType', value: 'all' } })}
                  className={`px-3 py-1 text-sm rounded-md ${
                    (filters.commissionType || 'all') === 'all' ? 'bg-blue-600 text-white' : 'text-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => handleFilterChange({ target: { name: 'commissionType', value: 'upfront' } })}
                  className={`px-3 py-1 text-sm rounded-md ${
                    filters.commissionType === 'upfront' ? 'bg-blue-600 text-white' : 'text-gray-300'
                  }`}
                >
                  Upfront
                </button>
                <button
                  onClick={() => handleFilterChange({ target: { name: 'commissionType', value: 'trail' } })}
                  className={`px-3 py-1 text-sm rounded-md ${
                    filters.commissionType === 'trail' ? 'bg-blue-600 text-white' : 'text-gray-300'
                  }`}
                >
                  Trail
                </button>
              </div>
            </div>
          )}

          {/* Performance Band Filter */}
          {featureFlags.performanceBands && canSeeAdminFilters && !hidePerformance && (
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Performance
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 rounded">NEW</span>
              </label>
              <select
                name="performanceBand"
                value={filters.performanceBand || 'all'}
                onChange={handleFilterChange}
                className="block w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 min-w-[150px]"
              >
                <option value="all">All Performers</option>
                <option value="top20">Top 20%</option>
                <option value="middle60">Middle 60%</option>
                <option value="bottom20">Bottom 20%</option>
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedDashboardFilters;
